import { Info } from "lucide-react";
import { motion } from "motion/react";
import type { ComponentType, ReactNode } from "react";

import { HEADLINE_COMPACT_FROM, useShortTokenFormat, useTokenFormat } from "./useTokenFormat";
import { BALANCE_EASE, AnimatedNumber } from "../AnimatedNumber";

import { Button } from "@/components/ui/v2/button";
import { Card } from "@/components/ui/v2/card";
import { metricCardVariants } from "@/components/ui/v2/metric-card-variants";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/v2/tooltip";
import { cn } from "@/lib/utils/cn";

export type TileEdit = { isEditing: boolean; onEdit: () => void; onDone: () => void };

const TILE_SURFACE = cn(metricCardVariants({ variant: "gradient" }), "relative h-full overflow-hidden px-5 py-5");

type TileProps = {
  id?: string;
  icon: ComponentType<{ className?: string }>;
  label: string;
  hint: string;
  corner?: ReactNode;
  pulse?: ReactNode;
  centered?: boolean;
  children: ReactNode;
};

export function Tile({ id, icon: Icon, label, hint, corner, pulse, centered = false, children }: TileProps) {
  return (
    <Card id={id} padding="sm" className={TILE_SURFACE} data-slot="metric-card" data-variant="gradient">
      {pulse}
      <div className="flex h-full gap-4">
        <span
          data-slot="metric-card-icon"
          className="grid size-14 shrink-0 place-items-center self-center rounded-full selected-tint-primary text-primary"
        >
          <Icon className="size-6" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex min-h-9 items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-1.5 text-base text-muted-foreground">
              <span data-slot="card-description" className="truncate">{label}</span>
              <TileHint hint={hint} />
            </span>
            {corner}
          </div>
          <motion.div
            layout="size"
            transition={{ duration: 0.18, ease: BALANCE_EASE }}
            className={cn("flex min-h-30 flex-1 flex-col", centered && "justify-center")}
          >
            {children}
          </motion.div>
        </div>
      </div>
    </Card>
  );
}

function TileHint({ hint }: { hint: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={hint}
            className="grid size-6 shrink-0 place-items-center rounded-full text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Info className="size-3.5" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px]">
          <p>{hint}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function TileSkeleton() {
  return (
    <Card padding="sm" className={TILE_SURFACE} data-slot="metric-card" data-variant="gradient">
      <div className="flex gap-4">
        <Skeleton className="size-14 shrink-0 self-center rounded-full" />
        <div className="flex flex-1 flex-col gap-3 py-1">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-2 w-full rounded-full" />
          <Skeleton className="h-3 w-40" />
        </div>
      </div>
    </Card>
  );
}

export function BigNumber({ value, qualifier }: { value: number; qualifier?: string }) {
  const formatTokens = useTokenFormat();
  const shortTokens = useShortTokenFormat(HEADLINE_COMPACT_FROM);
  return (
    <p data-slot="metric-card-value" className="flex flex-wrap items-baseline gap-x-3 text-foreground">
      <AnimatedNumber
        value={value}
        format={shortTokens}
        title={formatTokens(value)}
        className="text-4xl font-semibold tracking-tight"
      />
      {qualifier && <span className="text-lg text-muted-foreground tabular-nums">{qualifier}</span>}
    </p>
  );
}

type TileActionProps = {
  label: string;
  icon: ComponentType<{ className?: string }>;
  onClick: () => void;
};

export function TileAction({ label, icon: Icon, onClick }: TileActionProps) {
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className="h-9 shrink-0 gap-2 px-2.5 text-base font-semibold text-foreground hover-surface-control"
    >
      <Icon className="size-5" />
      {label}
    </Button>
  );
}
